import { Box, Text, useMantineColorScheme } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import classes from "../styles/PlansCard.module.css";

import { defaultCurrency } from "@/lib/currency";

import type { Currency } from "@/types/currency";
import type { FC } from "react";
import type { billingPeriodType } from "../types/const";

interface FooterProps {
  price?: number;
  billingPeriod: billingPeriodType;
}

export const Footer: FC<FooterProps> = ({ price = 0, billingPeriod }) => {
  const { t } = useTranslation();
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const textColor = isDark ? "secondary.1" : "secondary.6";

  if (!price) {
    return (
      <Text fz="h2" pt={8} fw={400} pos="relative" top={4}>
        {t("plans.free")}
      </Text>
    );
  }

  const monthlyPrice = price * (currency.value ?? 1);
  const yearlyPrice = monthlyPrice * 12;

  const formatPrice = (value: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.code,
      maximumFractionDigits: 0,
    }).format(value);

  return (
    <Box className={classes.footer}>
      {billingPeriod === "Monthly" ? (
        <Box pos="relative" top={4}>
          <Text fz="h2" mb={0} pb={0} fw={400}>
            {formatPrice(monthlyPrice)}
            <Text component="span" fz="xs" c={textColor} ml="xs">
              /{t("plans.permonth")}
            </Text>
          </Text>
          <Text component="span" fz="sm" c={textColor} fw={400} pos="relative" bottom={4}>
            {formatPrice(yearlyPrice)} /{t("plans.peryear")}
          </Text>
        </Box>
      ) : (
        <Box pos="relative" top={4}>
          <Text fz="h2" mb={0} pb={0} fw={400}>
            {formatPrice(yearlyPrice)}
            <Text component="span" fz="xs" c={textColor} ml="xs">
              /{t("plans.peryear")}
            </Text>
          </Text>
          <Text component="span" fz="sm" c={textColor} fw={400} pos="relative" bottom={4}>
            {formatPrice(monthlyPrice)} /{t("plans.permonth")}
          </Text>
        </Box>
      )}
    </Box>
  );
};
